import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {ApiDynamicArticle} from '@api/dynamic-articles';
import {AppState} from '@store/state/app.state';
import {Store} from '@ngrx/store';
import {setShoppingCartDynamicArticles} from '@store/actions/shopping-cart.action';
import {ShoppingCartService} from "@iw-shop/shopping-cart/services/shopping-cart.service";
import {take} from "rxjs/operators";
import {shoppingCartDynamicArticleIdsDeselect} from "@store/actions/storage.action";
import {NumberFormat} from "@iw-shop/core/functions/number-format";
import {Observable, Subject} from "rxjs";
import {Customer} from "@iw-shop/customer/models/customer";
import {getSelectedCustomer} from "@store/selectors/user.selector";
import {EnvironmentService} from "@iw-shop/core/services/environment.service";

@Component({
    selector: 'iw-shop-dynamic-article-items',
    standalone: false,
    templateUrl: './dynamic-article-items.component.html',
    styleUrls: ['./dynamic-article-items.component.scss']
})
export class DynamicArticleItemsComponent implements OnInit, OnDestroy {
    @Output() onSelectItem = new EventEmitter<ApiDynamicArticle>();
    @Output() onDeselectItem = new EventEmitter<ApiDynamicArticle>();
    @Output() isSelectableItem = new EventEmitter<boolean>();
    @Input() items?: ApiDynamicArticle[];
    @Input() writeable?: boolean = true;

    destroy$: Subject<boolean> = new Subject<boolean>();

    customer$?: Observable<Customer | null | undefined>;
    currency: 'EUR' | 'CHF' = 'EUR';

    resourceUrl?: any = null;

    constructor(private store: Store<AppState>, private shoppingCartService: ShoppingCartService,
                private environmentService: EnvironmentService) {
        this.store.select(state => state.config).pipe(take(1)).subscribe(c => this.currency = c.currency);
        this.customer$ = this.store.select(getSelectedCustomer);
    }

    ngOnInit() {
        this.resourceUrl = this.environmentService.environment.urlS3;
        if((this.getItems(['deliverySelectable', 'deliveryOverlay'])?.length ?? 0) > 0){
            this.isSelectableItem.emit(true);
        } else {
            this.isSelectableItem.emit(false);
        }
    }

    getItems(categories?: ApiDynamicArticle['category'][], excludeCategories?: ApiDynamicArticle['category'][]): ApiDynamicArticle[] | undefined {
        return this.items
            ?.filter(x => categories?.includes(x.category) || categories?.length == 0)
            .filter(x => !excludeCategories?.includes(x.category));
    }

    toggleItem(id: string, $event: any) {
        const isChecked = $event.target.checked;
        const item: ApiDynamicArticle | undefined = this.items?.filter(i => i.id == id)[0];
        if (item) {
            let dynamicArticles: { id: string, amount: number }[] = [{amount: isChecked ? 1 : 0, id: item.id}];
            // Wird benötigt, wenn optionale Artikel automatisch dem Warenkorb hinzugefügt werden
            let disabledDynamicArticleIds: string[] = [];

            this.shoppingCartService.getDeselectedDynamicArticles().map(
                d => {
                    disabledDynamicArticleIds.push(d.id);
                }
            )

            this.items?.filter(i => i.id != id).map(
                a => {
                    if (a.category == 'deliverySelectable' || a.category == 'deliveryOverlay') {
                        dynamicArticles.push({id: a.id, amount: 0});
                        let existDynamicArticleId = disabledDynamicArticleIds.filter(a => a == id)[0];
                        if (!existDynamicArticleId) {
                            disabledDynamicArticleIds.push(id);
                        }
                    } else {
                        let amount = 0;
                        if (this.shoppingCartService.isDynamicArticleInCart(a.id)) {
                            amount = 1;
                        }
                        dynamicArticles.push({id: a.id, amount});
                    }
                }
            );

            if (isChecked) {
                disabledDynamicArticleIds = disabledDynamicArticleIds.filter(a => a !== id);
                this.onSelectItem.emit(item);
            } else {
                let existDynamicArticleId = disabledDynamicArticleIds.filter(a => a == id)[0];
                if (!existDynamicArticleId &&
                    item.addAutomatically) {
                    disabledDynamicArticleIds.push(id);
                }
                this.onDeselectItem.emit(item);
            }
            this.store.dispatch(shoppingCartDynamicArticleIdsDeselect({ids: disabledDynamicArticleIds}));
            this.store.dispatch(setShoppingCartDynamicArticles({jsonId: 'dynamicArticleItems.toggleItem', dynamicArticles}));
        }
    }

    _onSelectItem(id: string, $event: any) {
        const item: ApiDynamicArticle | undefined = this.items?.filter(i => i.id == id)[0];
        if($event.target.checked
            && item &&
            this.shoppingCartService.isDynamicArticleInCart(id)
        ){
            this.onSelectItem.emit(item)
        }
    }

    isDynamicArticleInCart(id: string): boolean {
        return this.shoppingCartService.isDynamicArticleInCart(id);
    }

    ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }

    protected readonly NumberFormat = NumberFormat;
}
